export type ShapeGender = "female" | "male";

export type FemaleShape = "hourglass" | "pear" | "apple" | "rectangle" | "inverted_triangle";
export type MaleShape = "trapezoid" | "rectangle" | "triangle" | "inverted_triangle" | "oval";
export type AnyShape = FemaleShape | MaleShape;

export interface BodyShapeInput {
  gender: ShapeGender;
  bustCm: number; // chest for male
  waistCm: number;
  hipsCm: number;
  highHipCm?: number;
  shouldersCm?: number;
}

export interface BodyShapeResult {
  shape: AnyShape;
  bustHipRatio: number; // bust / hip
  waistHipRatio: number; // waist / hip
  waistBustRatio: number; // waist / bust
  bustHipDiffPct: number; // (bust - hip) / max * 100
  waistDefinedPct: number; // 1 - waist/max(bust,hip)
}

export function validate(i: Partial<BodyShapeInput>): { ok: boolean; error?: string } {
  if (!i.bustCm || !i.waistCm || !i.hipsCm) return { ok: false, error: "Enter bust, waist and hips" };
  for (const [k, v] of [["bust", i.bustCm], ["waist", i.waistCm], ["hips", i.hipsCm]] as const) {
    if (!Number.isFinite(v as number) || (v as number) < 30 || (v as number) > 250) return { ok: false, error: `${k} out of range` };
  }
  return { ok: true };
}

export function computeBodyShape(input: BodyShapeInput): BodyShapeResult {
  const { gender, bustCm, waistCm, hipsCm, shouldersCm } = input;
  const top = Math.max(shouldersCm ?? 0, bustCm);
  const max = Math.max(top, hipsCm);
  const bustHipDiffPct = ((bustCm - hipsCm) / Math.max(bustCm, hipsCm)) * 100;
  const topHipDiffPct = ((top - hipsCm) / Math.max(top, hipsCm)) * 100;
  const waistDefinedPct = 1 - waistCm / max;

  let shape: AnyShape;
  if (gender === "female") {
    if (hipsCm > bustCm * 1.05) {
      shape = "pear";
    } else if (top > hipsCm * 1.07) {
      shape = "inverted_triangle";
    } else if (waistDefinedPct >= 0.25) {
      shape = "hourglass";
    } else if (waistCm >= hipsCm * 0.97 || waistCm >= bustCm * 0.97) {
      shape = "apple";
    } else {
      shape = "rectangle";
    }
  } else {
    // male
    const sim = (a: number, b: number) => Math.abs(a - b) / Math.max(a, b) <= 0.05;
    const sh = shouldersCm ?? bustCm;
    if (waistCm > sh && waistCm > hipsCm) shape = "oval";
    else if (sh > hipsCm * 1.1 && sh > waistCm * 1.1) shape = "inverted_triangle";
    else if (sh > waistCm && waistCm > hipsCm * 0.95 && sh > hipsCm * 1.05) shape = "trapezoid";
    else if (hipsCm > sh * 1.05) shape = "triangle";
    else if (sim(sh, waistCm) && sim(waistCm, hipsCm)) shape = "rectangle";
    else if (sh > hipsCm * 1.05 || topHipDiffPct > 5) shape = "trapezoid";
    else shape = "rectangle";
  }

  return {
    shape,
    bustHipRatio: bustCm / hipsCm,
    waistHipRatio: waistCm / hipsCm,
    waistBustRatio: waistCm / bustCm,
    bustHipDiffPct,
    waistDefinedPct,
  };
}
